import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { Button, Card, Badge, Row, Col, Alert, Modal } from 'react-bootstrap';
import * as Yup from 'yup';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import homepageApi from '../../api/homepage';
import CustomInput from '../../common/FormComponents/Input';
import DeleteConfirmationModal from '../../common/UI/CustomModal/deleteModal';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import { getFormDataFromObject } from '../../utils/helpers';
import ReactSwitch from 'react-switch';
import Loader from '../../common/UI/Loader';
import ThemeEditor from './ThemeEditor';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  rating: Yup.number()
    .required('Rating is required')
    .min(1, 'Rating must be between 1 and 5')
    .max(5, 'Rating must be between 1 and 5'),
  review: Yup.string().required('Review is required'),
});

const TestimonialsManager = ({ section, backToList, refreshSections }) => {
  const [items, setItems] = useState(section.items || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);

  const sortedItems = [...items].sort((a, b) => a.order - b.order);

  const handleAddNew = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = (itemId) => {
    setDeleteItemId(itemId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await homepageApi.deleteItemFromSection(section.sectionKey, deleteItemId);
      toastSuccess('Testimonial deleted successfully');
      refreshSections();
      fetchItems();
    } catch (error) {
      toastError('Failed to delete testimonial');
      console.error(error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await homepageApi.getHomepageSection(section.sectionKey);
      setItems(response?.data?.data?.items || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleStatus = async (itemId, currentStatus) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({ status: !currentStatus });
      await homepageApi.updateItemInSection(
        section.sectionKey,
        itemId,
        payload,
      );
      toastSuccess('Status updated successfully');
      fetchItems();
    } catch (error) {
      toastError('Failed to update status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      let payload = {
        name: values.name,
        rating: parseInt(values.rating),
        review: values.review,
        verified: values.verified,
        date: values.date,
        order: values.order || 0,
        status: values.status !== undefined ? values.status : true,
      };

      payload = getFormDataFromObject(payload);

      if (editingItem) {
        await homepageApi.updateItemInSection(
          section.sectionKey,
          editingItem._id,
          payload,
        );
        toastSuccess('Testimonial updated successfully');
      } else {
        await homepageApi.addItemToSection(section.sectionKey, payload);
        toastSuccess('Testimonial added successfully');
        resetForm();
      }

      setShowModal(false);
      setEditingItem(null);
      fetchItems();
      refreshSections();
    } catch (error) {
      toastError(
        editingItem
          ? 'Failed to update testimonial'
          : 'Failed to add testimonial',
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(sortedItems);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    const updatedItems = reorderedItems.map((item, index) => ({
      _id: item._id,
      order: index,
    }));

    setItems(reorderedItems.map((item, index) => ({ ...item, order: index })));

    try {
      await homepageApi.reorderItems(section.sectionKey, {
        items: updatedItems,
      });
      toastSuccess('Order updated successfully');
    } catch (error) {
      toastError('Failed to update order');
      fetchItems();
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <i
        key={i}
        className={`fa fa-star${i < rating ? '' : '-o'}`}
        style={{ color: '#ffc107' }}
      ></i>
    ));
  };

  if (loading && !items.length) {
    return <Loader loading={loading} />;
  }

  return (
    <div className="content_wrapper all_products_main">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer' }}>
                <i className="fa fa-home me-2"></i>Landing Page CMS
              </li>
              <li className="active">Testimonials Management</li>
            </ol>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              {/* Enhanced Header */}
              <div className="header_top border-bottom pb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className="mb-2">
                      <i className="fa fa-comments me-2 text-primary"></i>
                      Testimonials Management
                    </h3>
                    <p className="text-muted mb-2">
                      Manage customer reviews and testimonials displayed on your
                      homepage
                    </p>
                    <div className="d-flex gap-2 align-items-center">
                      <Badge bg="primary" className="text-uppercase">
                        List Section
                      </Badge>
                      <Badge bg="secondary" className="font-monospace">
                        {section.sectionKey}
                      </Badge>
                      <Badge bg="info">{sortedItems.length} Testimonials</Badge>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => setShowThemeEditor(true)}>
                      <i className="fa fa-palette me-2"></i>Theme
                    </Button>
                    <Button
                      variant="success"
                      onClick={handleAddNew}
                      disabled={loading}
                    >
                      <i className="fa fa-plus me-2"></i>Add New
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={backToList}
                    >
                      <i className="fa fa-arrow-left me-2"></i>Back
                    </Button>
                  </div>
                </div>
              </div>

              <div className="middle_main pt-4">
                {sortedItems.length === 0 ? (
                  <Card className="text-center py-5 border-0 bg-light">
                    <Card.Body>
                      <i className="fa fa-comments fa-5x text-muted mb-4 d-block"></i>
                      <h4 className="text-muted mb-2">No Testimonials Yet</h4>
                      <p className="text-muted mb-4">
                        Start building trust by adding customer testimonials
                      </p>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleAddNew}
                      >
                        <i className="fa fa-plus me-2"></i>Add Your First
                        Testimonial
                      </Button>
                    </Card.Body>
                  </Card>
                ) : (
                  <>
                    <Alert variant="info" className="mb-4">
                      <i className="fa fa-info-circle me-2"></i>
                      <strong>Tip:</strong> Drag cards to reorder testimonials.
                      Toggle the switch to show/hide on website.
                    </Alert>

                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="testimonials">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="row"
                            style={{
                              backgroundColor: snapshot.isDraggingOver
                                ? '#f8f9fa'
                                : 'transparent',
                              padding: '10px',
                              borderRadius: '8px',
                              transition: 'background-color 0.2s ease',
                            }}
                          >
                            {sortedItems.map((item, index) => (
                              <Draggable
                                key={item._id}
                                draggableId={item._id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    className="col-lg-6 col-md-12 mb-4"
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                  >
                                    <Card
                                      className="h-100 shadow-sm border"
                                      style={{
                                        backgroundColor: snapshot.isDragging
                                          ? '#e3f2fd'
                                          : 'white',
                                        transform: snapshot.isDragging
                                          ? 'rotate(2deg)'
                                          : 'none',
                                        transition: 'all 0.2s ease',
                                      }}
                                    >
                                      <Card.Header className="bg-light border-bottom">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <div
                                              {...provided.dragHandleProps}
                                              style={{ cursor: 'grab' }}
                                            >
                                              <i className="fa fa-grip-vertical text-muted"></i>
                                            </div>
                                            <Badge bg="primary" pill>
                                              #{index + 1}
                                            </Badge>
                                            <strong className="text-dark">
                                              {item.name}
                                            </strong>
                                            {item.verified && (
                                              <Badge bg="success">
                                                <i className="fa fa-check-circle me-1"></i>
                                                Verified
                                              </Badge>
                                            )}
                                          </div>
                                          <ReactSwitch
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            onChange={() =>
                                              handleToggleStatus(
                                                item._id,
                                                item.status,
                                              )
                                            }
                                            checked={item.status}
                                            height={20}
                                            width={40}
                                            onColor="#28a745"
                                            offColor="#6c757d"
                                          />
                                        </div>
                                      </Card.Header>

                                      <Card.Body>
                                        <Row>
                                          <Col md={12} className="mb-3">
                                            <div className="mb-2">
                                              {renderStars(item.rating)}
                                              <small className="text-muted ms-2">
                                                ({item.rating}/5)
                                              </small>
                                            </div>
                                            <blockquote className="mb-0">
                                              <p
                                                className="mb-2"
                                                style={{
                                                  fontStyle: 'italic',
                                                  color: '#555',
                                                }}
                                              >
                                                &quot;{item.review}&quot;
                                              </p>
                                              {item.date && (
                                                <footer className="blockquote-footer">
                                                  <small>{item.date}</small>
                                                </footer>
                                              )}
                                            </blockquote>
                                          </Col>

                                          <Col md={12}>
                                            <div className="d-flex justify-content-between align-items-center">
                                              <div>
                                                {item.status ? (
                                                  <Badge bg="success">
                                                    <i className="fa fa-eye me-1"></i>
                                                    Visible
                                                  </Badge>
                                                ) : (
                                                  <Badge bg="secondary">
                                                    <i className="fa fa-eye-slash me-1"></i>
                                                    Hidden
                                                  </Badge>
                                                )}
                                              </div>
                                              <div className="d-flex gap-2">
                                                <Button
                                                  variant="outline-primary"
                                                  size="sm"
                                                  onClick={() =>
                                                    handleEdit(item)
                                                  }
                                                >
                                                  <i className="fa fa-edit me-1"></i>
                                                  Edit
                                                </Button>
                                                <Button
                                                  variant="outline-danger"
                                                  size="sm"
                                                  onClick={() =>
                                                    handleDelete(item._id)
                                                  }
                                                >
                                                  <i className="fa fa-trash me-1"></i>
                                                  Delete
                                                </Button>
                                              </div>
                                            </div>
                                          </Col>
                                        </Row>
                                      </Card.Body>
                                    </Card>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Add/Edit Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <i
              className={`fa fa-${
                editingItem ? 'edit' : 'plus'
              } me-2 text-primary`}
            ></i>
            {editingItem ? 'Edit' : 'Add'} Testimonial
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            name: editingItem?.name || '',
            rating: editingItem?.rating || 5,
            review: editingItem?.review || '',
            verified: editingItem?.verified || false,
            date: editingItem?.date || '',
            order: editingItem?.order || 0,
            status: editingItem ? editingItem.status : true,
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form>
              <Modal.Body>
                <Card className="mb-3 border-0 bg-light">
                  <Card.Body className="py-2">
                    <small className="text-muted">
                      <i className="fa fa-lightbulb text-warning me-2"></i>
                      Create authentic testimonials that build trust and
                      credibility with your audience
                    </small>
                  </Card.Body>
                </Card>

                <Row>
                  <Col md={8} className="mb-3">
                    <label className="form-label">
                      <i className="fa fa-user me-2 text-primary"></i>
                      Customer Name <span className="text-danger">*</span>
                    </label>
                    <CustomInput
                      name="name"
                      placeholder="e.g., Sarah Johnson"
                      className="form-control"
                      isRequired
                    />
                    <small className="text-muted">
                      Full name of the customer
                    </small>
                  </Col>

                  <Col md={4} className="mb-3">
                    <label className="form-label">
                      <i className="fa fa-star me-2 text-primary"></i>
                      Rating <span className="text-danger">*</span>
                    </label>
                    <CustomInput
                      name="rating"
                      type="number"
                      placeholder="5"
                      className="form-control"
                      min="1"
                      max="5"
                      isRequired
                    />
                    <small className="text-muted">1-5 stars</small>
                  </Col>

                  <Col md={12} className="mb-3">
                    <label className="form-label">
                      <i className="fa fa-comment me-2 text-primary"></i>
                      Review Text <span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="review"
                      placeholder="Amazing service! The team was professional and caring..."
                      className="form-control"
                      rows="4"
                      value={formik.values.review}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.review && formik.errors.review && (
                      <div className="text-danger small mt-1">
                        {formik.errors.review}
                      </div>
                    )}
                    <small className="text-muted">
                      The customer&quot;s feedback (keep it concise and
                      impactful)
                    </small>
                  </Col>

                  <Col md={6} className="mb-3">
                    <label className="form-label">
                      <i className="fa fa-calendar me-2 text-primary"></i>
                      Date/Timeframe
                    </label>
                    <CustomInput
                      name="date"
                      placeholder="e.g., 2 weeks ago"
                      className="form-control"
                    />
                    <small className="text-muted">
                      When the review was given
                    </small>
                  </Col>

                  <Col md={6} className="mb-3">
                    <label className="form-label">
                      <i className="fa fa-sort-numeric-up me-2 text-primary"></i>
                      Display Order
                    </label>
                    <CustomInput
                      name="order"
                      type="number"
                      placeholder="0"
                      className="form-control"
                    />
                    <small className="text-muted">
                      Lower numbers appear first
                    </small>
                  </Col>

                  <Col md={6} className="mb-3">
                    <label className="form-label d-block">
                      <i className="fa fa-check-circle me-2 text-primary"></i>
                      Verified Customer
                    </label>
                    <ReactSwitch
                      uncheckedIcon={false}
                      checkedIcon={false}
                      onChange={(checked) =>
                        formik.setFieldValue('verified', checked)
                      }
                      checked={formik.values.verified}
                      height={24}
                      width={48}
                      onColor="#28a745"
                    />
                    <small className="text-muted d-block mt-2">
                      Show verified badge
                    </small>
                  </Col>

                  <Col md={6} className="mb-3">
                    <label className="form-label d-block">
                      <i className="fa fa-toggle-on me-2 text-primary"></i>
                      Active Status
                    </label>
                    <ReactSwitch
                      uncheckedIcon={false}
                      checkedIcon={false}
                      onChange={(checked) =>
                        formik.setFieldValue('status', checked)
                      }
                      checked={formik.values.status}
                      height={24}
                      width={48}
                      onColor="#28a745"
                    />
                    <small className="text-muted d-block mt-2">
                      Display on website
                    </small>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer className="bg-light">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingItem(null);
                  }}
                >
                  <i className="fa fa-times me-2"></i>Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>Saving...
                    </>
                  ) : (
                    <>
                      <i
                        className={`fa fa-${
                          editingItem ? 'save' : 'plus'
                        } me-2`}
                      ></i>
                      {editingItem ? 'Update' : 'Add'} Testimonial
                    </>
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      <DeleteConfirmationModal
        name="Testimonial"
        show={showDeleteModal}
        handleToggle={() => setShowDeleteModal(false)}
        handleDelete={confirmDelete}
      />

      <ThemeEditor
        show={showThemeEditor}
        onHide={() => setShowThemeEditor(false)}
        sectionKey={section.sectionKey}
        sectionName="Testimonials"
      />
    </div>
  );
};

export default TestimonialsManager;
